import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Plus, Minus, Package, Truck, Settings, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCart } from '../../Context/CartContext';
import { useAuth } from '../../Context/AuthContext';
import productService from '../../services/productService';

const Cart = () => {
  const { cartItems, isCartOpen, closeCart, updateQuantity, removeFromCart } = useCart();
  const { user } = useAuth();
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [loadingRecommended, setLoadingRecommended] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Prevent body scroll when cart is open
  useEffect(() => {
    if (isCartOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isCartOpen]);

  // Fetch recommended products when cart opens
  useEffect(() => {
    const fetchRecommendedProducts = async () => {
      if (isCartOpen && cartItems.length > 0) {
        setLoadingRecommended(true);
        try {
          // Fetch homepage data to get trending/new arrivals
          const response = await productService.getHomepageData();
          if (response.success) {
            // Get products from trending or new arrivals, exclude items already in cart
            let products = [];
            if (response.trendingProducts && response.trendingProducts.length > 0) {
              products = response.trendingProducts;
            } else if (response.newArrivals && response.newArrivals.length > 0) {
              products = response.newArrivals;
            } else {
              // Fallback: fetch all products
              const productsResponse = await productService.getProducts();
              if (productsResponse.success) {
                products = productsResponse.products;
              }
            }
            
            // Filter out products already in cart
            const cartProductIds = cartItems.map(item => item.product._id);
            const filteredProducts = products
              .filter(product => !cartProductIds.includes(product._id))
              .slice(0, 5); // Limit to 5 products
            
            setRecommendedProducts(filteredProducts);
            setCurrentSlide(0); // Reset slide when products change
          }
        } catch (error) {
          console.error('Error fetching recommended products:', error);
        } finally {
          setLoadingRecommended(false);
        }
      } else if (!isCartOpen) {
        // Clear recommended products when cart closes
        setRecommendedProducts([]);
        setCurrentSlide(0);
      }
    };

    fetchRecommendedProducts();
  }, [isCartOpen, cartItems]);

  // Calculate subtotal (only if user is logged in)
  const getSubtotal = () => {
    if (!user) return null;
    return cartItems.reduce((total, item) => {
      const price = parseFloat(item.product.msrp) || 0;
      return total + price * item.quantity;
    }, 0);
  };

  const subtotal = getSubtotal();

  return (
    <>
      {/* Overlay */}
      {isCartOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[90] transition-opacity duration-300"
          onClick={closeCart}
        />
      )}

      {/* Cart Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[480px] bg-white shadow-2xl z-[100] transform transition-transform duration-300 ease-in-out flex flex-col ${
          isCartOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-medium">Shopping cart</h2>
          <button
            onClick={closeCart}
            className="hover:text-gray-600 transition-colors"
            aria-label="Close cart"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Cart Content */}
        <div className="flex-grow overflow-y-auto">
          {cartItems.length === 0 ? (
            /* Empty Cart Message */
            <div className="flex flex-col items-center justify-center h-full px-6 text-center">
              <div className="mb-6">
                <Package className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-medium mb-2">Your cart is empty</h3>
                <p className="text-gray-600 text-sm">
                  You may check out all the available products and buy some in the shop
                </p>
              </div>
              <Link
                to="/collections"
                onClick={closeCart}
                className="bg-black text-white px-8 py-3 hover:bg-gray-800 transition-colors"
              >
                Return to shop
              </Link>
            </div>
          ) : (
            <div className="p-6">
              {/* Cart Items */}
              <div className="space-y-6 mb-6">
                {cartItems.map((item, index) => (
                  <div key={`${item.product._id}-${item.selectedColor}-${item.selectedSize}-${index}`} className="flex gap-4">
                    {/* Product Image */}
                    <Link
                      to={`/product/${item.product._id}`}
                      onClick={closeCart}
                      className="flex-shrink-0"
                    >
                      <img
                        src={
                          item.product.images && item.product.images[0]
                            ? item.product.images[0].path
                            : 'https://images.pexels.com/photos/5710082/pexels-photo-5710082.jpeg?auto=compress&cs=tinysrgb&w=300'
                        }
                        alt={item.product.productTitle}
                        className="w-24 h-24 object-cover bg-gray-100"
                      />
                    </Link>

                    {/* Product Details */}
                    <div className="flex-grow">
                      <Link
                        to={`/product/${item.product._id}`}
                        onClick={closeCart}
                        className="block hover:text-[#6B9FA8] transition-colors"
                      >
                        <h3 className="font-medium text-sm mb-1 line-clamp-2">
                          {item.product.productTitle}
                        </h3>
                      </Link>
                      <p className="text-sm text-gray-600 mb-2">
                        {item.selectedColor} / {item.selectedSize}
                      </p>
                      <p className="font-semibold text-base mb-3">
                        {user && item.product.msrp
                          ? `$${parseFloat(item.product.msrp).toFixed(2)}`
                          : 'Login to View Price'}
                      </p>

                      {/* Quantity Controls */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center border border-gray-300">
                          <button
                            onClick={() =>
                              updateQuantity(
                                item.product._id,
                                item.selectedColor,
                                item.selectedSize,
                                item.quantity - 1
                              )
                            }
                            className="px-3 py-1 hover:bg-gray-100 transition-colors"
                            aria-label="Decrease quantity"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="px-4 py-1 border-x border-gray-300 min-w-[50px] text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() =>
                              updateQuantity(
                                item.product._id,
                                item.selectedColor,
                                item.selectedSize,
                                item.quantity + 1
                              )
                            }
                            className="px-3 py-1 hover:bg-gray-100 transition-colors"
                            aria-label="Increase quantity"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>

                        <button
                          onClick={() =>
                            removeFromCart(item.product._id, item.selectedColor, item.selectedSize)
                          }
                          className="text-sm text-gray-600 hover:text-red-600 underline transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* You may also like section */}
              {(loadingRecommended || recommendedProducts.length > 0) && (
                <div className="border-t border-gray-200 pt-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium">You may also like</h3>
                    {!loadingRecommended && recommendedProducts.length > 1 && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
                          disabled={currentSlide === 0}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          aria-label="Previous product"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setCurrentSlide(Math.min(recommendedProducts.length - 1, currentSlide + 1))}
                          disabled={currentSlide >= recommendedProducts.length - 1}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          aria-label="Next product"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {loadingRecommended ? (
                    <div className="relative overflow-hidden">
                      <div className="animate-pulse">
                        {/* Skeleton Image */}
                        <div className="w-full h-32 bg-gray-200 rounded mb-3"></div>
                        {/* Skeleton Title */}
                        <div className="h-4 bg-gray-200 rounded mb-2 w-3/4"></div>
                        <div className="h-4 bg-gray-200 rounded mb-1 w-1/2"></div>
                        {/* Skeleton Price */}
                        <div className="h-3 bg-gray-200 rounded w-1/3 mt-2"></div>
                      </div>
                    </div>
                  ) : (
                    <div className="relative overflow-hidden">
                      <div
                        className="flex transition-transform duration-300 ease-in-out"
                        style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                      >
                        {recommendedProducts.map((product) => (
                          <div
                            key={product._id}
                            className="min-w-full flex-shrink-0"
                          >
                            <Link
                              to={`/product/${product._id}`}
                              onClick={closeCart}
                              className="group relative block"
                            >
                              <div className="relative mb-3">
                                <img
                                  src={
                                    product.images && product.images[0]
                                      ? product.images[0].path
                                      : 'https://images.pexels.com/photos/5710082/pexels-photo-5710082.jpeg?auto=compress&cs=tinysrgb&w=300'
                                  }
                                  alt={product.productTitle}
                                  className="w-full h-32 object-cover bg-gray-100"
                                />
                                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <div className="bg-white rounded-full p-2 shadow-lg">
                                    <Eye className="w-4 h-4 text-gray-700" />
                                  </div>
                                </div>
                              </div>
                              <h4 className="text-sm font-medium mb-1 line-clamp-2 group-hover:text-[#6B9FA8] transition-colors">
                                {product.productTitle}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {user && product.msrp
                                  ? `From $${parseFloat(product.msrp).toFixed(2)}`
                                  : 'From Login to View Price'}
                              </p>
                            </Link>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Feature Icons */}
              <div className="flex items-center justify-around py-6 border-y border-gray-200 mb-6">
                <div className="text-center">
                  <Package className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                </div>
                <div className="text-center">
                  <Truck className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                </div>
                <div className="text-center">
                  <Settings className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                </div>
              </div>

              {/* Subtotal */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg font-medium">Subtotal</span>
                  <span className="text-lg font-semibold">
                    {user && subtotal !== null
                      ? `$${subtotal.toFixed(2)}`
                      : 'Login to View Price'}
                  </span>
                </div>
                <p className="text-xs text-gray-600">
                  Taxes and{' '}
                  <span className="underline cursor-pointer">shipping</span> calculated at checkout
                </p>
              </div>

              {/* Terms and Conditions */}
              <div className="mb-6">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreeTerms}
                    onChange={(e) => setAgreeTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">
                    I agree with the{' '}
                    <Link
                      to="/terms"
                      className="underline hover:text-[#6B9FA8]"
                      onClick={closeCart}
                    >
                      terms and conditions
                    </Link>
                  </span>
                </label>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={closeCart}
                  className="border border-gray-900 text-gray-900 py-3 px-6 hover:bg-gray-100 transition-colors font-medium"
                >
                  View cart
                </button>
                <button
                  disabled={!agreeTerms}
                  className={`bg-black text-white py-3 px-6 font-medium transition-colors ${
                    agreeTerms ? 'hover:bg-gray-800' : 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  Check out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Cart;
